import { ColorPreset, ColumnDataType, ColumnDef, ConditionalFormatRule, FormatOperator, FormatsState } from './types';

export const COLOR_PRESETS: Record<
  ColorPreset,
  { bg: string; text: string; border: string; swatch: string; icon: string }
> = {
  yellow: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-900',
    border: 'border-yellow-300',
    swatch: 'bg-yellow-200 text-yellow-900 border-yellow-300',
    icon: 'text-yellow-500',
  },
  red: {
    bg: 'bg-red-100',
    text: 'text-red-900',
    border: 'border-red-300',
    swatch: 'bg-red-200 text-red-900 border-red-300',
    icon: 'text-red-500',
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-900',
    border: 'border-green-300',
    swatch: 'bg-green-200 text-green-900 border-green-300',
    icon: 'text-emerald-500',
  },
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-900',
    border: 'border-blue-300',
    swatch: 'bg-blue-200 text-blue-900 border-blue-300',
    icon: 'text-blue-500',
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-900',
    border: 'border-purple-300',
    swatch: 'bg-purple-200 text-purple-900 border-purple-300',
    icon: 'text-purple-500',
  },
  orange: {
    bg: 'bg-orange-100',
    text: 'text-orange-900',
    border: 'border-orange-300',
    swatch: 'bg-orange-200 text-orange-900 border-orange-300',
    icon: 'text-orange-500',
  },
  pink: {
    bg: 'bg-pink-100',
    text: 'text-pink-900',
    border: 'border-pink-300',
    swatch: 'bg-pink-200 text-pink-900 border-pink-300',
    icon: 'text-pink-500',
  },
  gray: {
    bg: 'bg-slate-100',
    text: 'text-slate-900',
    border: 'border-slate-300',
    swatch: 'bg-slate-200 text-slate-900 border-slate-300',
    icon: 'text-slate-500',
  },
};

export const COLOR_PRESET_ORDER: ColorPreset[] = [
  'yellow', 'red', 'green', 'blue', 'purple', 'orange', 'pink', 'gray',
];

export function operatorLabel(op: FormatOperator, dataType: ColumnDataType): string {
  if (dataType === 'date') {
    switch (op) {
      case 'eq': return 'on';
      case 'neq': return 'not on';
      case 'gt': return 'after';
      case 'lt': return 'before';
      case 'gte': return 'on or after';
      case 'lte': return 'on or before';
      case 'between': return 'between';
      case 'contains': return 'contains';
    }
  }
  switch (op) {
    case 'eq': return '=';
    case 'neq': return '≠';
    case 'gt': return '>';
    case 'lt': return '<';
    case 'gte': return '≥';
    case 'lte': return '≤';
    case 'between': return 'between';
    case 'contains': return 'contains';
  }
}

export function allowedOperators(dataType: ColumnDataType): FormatOperator[] {
  switch (dataType) {
    case 'number': return ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'between'];
    case 'date': return ['eq', 'neq', 'lt', 'gt', 'lte', 'gte', 'between'];
    case 'enum': return ['eq', 'neq'];
    case 'string':
    default: return ['eq', 'neq', 'contains'];
  }
}

const MS_PER_DAY = 86_400_000;

function startOfTodayMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function startOfWeekMs(): number {
  // Week starts on Sunday (locale-agnostic, simple choice).
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d.getTime();
}

function startOfMonthMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d.getTime();
}

function startOfYearMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(0, 1);
  return d.getTime();
}

/**
 * Parse either an absolute date string or a relative-date token into a ms
 * timestamp. Supported tokens:
 *
 *   @today
 *   @days-ago:N
 *   @days-from-now:N
 *   @start-of-week
 *   @start-of-month
 *   @start-of-year
 *
 * Relative tokens resolve "now" each call so saved filters/formats stay
 * current.
 */
export function parseDateValue(raw: string): number {
  if (!raw) return NaN;

  if (raw.startsWith('@')) {
    if (raw === '@today') return startOfTodayMs();
    if (raw === '@start-of-week') return startOfWeekMs();
    if (raw === '@start-of-month') return startOfMonthMs();
    if (raw === '@start-of-year') return startOfYearMs();
    const ago = raw.match(/^@days-ago:(\d+)$/);
    if (ago) return startOfTodayMs() - parseInt(ago[1], 10) * MS_PER_DAY;
    const fwd = raw.match(/^@days-from-now:(\d+)$/);
    if (fwd) return startOfTodayMs() + parseInt(fwd[1], 10) * MS_PER_DAY;
    return NaN;
  }

  // Absolute: accept YYYY-MM-DD (from <input type=date>) or M/D/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  const parts = raw.split('/').map(Number);
  if (parts.length === 3 && !parts.some(Number.isNaN)) {
    const [m, d, y] = parts;
    return new Date(y, (m || 1) - 1, d || 1).getTime();
  }
  const ts = Date.parse(raw);
  return Number.isFinite(ts) ? ts : NaN;
}

/**
 * Friendly label for a date value (absolute or relative token). Used by
 * picker triggers and badges.
 */
export function describeDateValue(raw: string): string {
  if (!raw) return '';
  if (raw === '@today') return 'Today';
  if (raw === '@start-of-week') return 'Start of week';
  if (raw === '@start-of-month') return 'Start of month';
  if (raw === '@start-of-year') return 'Start of year';
  const ago = raw.match(/^@days-ago:(\d+)$/);
  if (ago) return `${ago[1]} day${ago[1] === '1' ? '' : 's'} ago`;
  const fwd = raw.match(/^@days-from-now:(\d+)$/);
  if (fwd) return `${fwd[1]} day${fwd[1] === '1' ? '' : 's'} from now`;
  // Absolute: render as locale string
  const ts = parseDateValue(raw);
  if (!Number.isFinite(ts)) return raw;
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function matchesRule<T>(
  row: T,
  col: ColumnDef<T>,
  rule: ConditionalFormatRule
): boolean {
  const dataType: ColumnDataType = col.dataType ?? 'string';

  if (dataType === 'number') {
    const a = col.numericValue ? col.numericValue(row) : Number(col.accessor(row));
    const b = Number(rule.value);
    const b2 = rule.value2 !== undefined ? Number(rule.value2) : NaN;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    switch (rule.operator) {
      case 'eq': return a === b;
      case 'neq': return a !== b;
      case 'gt': return a > b;
      case 'lt': return a < b;
      case 'gte': return a >= b;
      case 'lte': return a <= b;
      case 'between': {
        if (!Number.isFinite(b2)) return false;
        const lo = Math.min(b, b2);
        const hi = Math.max(b, b2);
        return a >= lo && a <= hi;
      }
      default: return false;
    }
  }

  if (dataType === 'date') {
    const a = col.numericValue ? col.numericValue(row) : parseDateValue(col.accessor(row));
    if (!Number.isFinite(a)) return false;
    const aDay = Math.floor(a / MS_PER_DAY);

    if (rule.operator === 'between') {
      const b = parseDateValue(rule.value);
      const b2 = rule.value2 ? parseDateValue(rule.value2) : NaN;
      if (!Number.isFinite(b) || !Number.isFinite(b2)) return false;
      const lo = Math.floor(Math.min(b, b2) / MS_PER_DAY);
      const hi = Math.floor(Math.max(b, b2) / MS_PER_DAY);
      return aDay >= lo && aDay <= hi;
    }

    const b = parseDateValue(rule.value);
    if (!Number.isFinite(b)) return false;
    const bDay = Math.floor(b / MS_PER_DAY);
    switch (rule.operator) {
      case 'eq': return aDay === bDay;
      case 'neq': return aDay !== bDay;
      case 'gt': return aDay > bDay;
      case 'lt': return aDay < bDay;
      case 'gte': return aDay >= bDay;
      case 'lte': return aDay <= bDay;
      default: return false;
    }
  }

  const a = col.accessor(row) ?? '';
  const b = rule.value ?? '';
  switch (rule.operator) {
    case 'eq': return a === b;
    case 'neq': return a !== b;
    case 'contains': return a.toLowerCase().includes(b.toLowerCase());
    case 'gt': return a > b;
    case 'lt': return a < b;
    case 'gte': return a >= b;
    case 'lte': return a <= b;
    default: return false;
  }
}

export function applyDateFilter<T>(
  rows: T[],
  col: ColumnDef<T>,
  filter: import('./types').DateFilter
): T[] {
  return rows.filter((r) => matchesDateFilter(r, col, filter));
}

export function matchesDateFilter<T>(
  row: T,
  col: ColumnDef<T>,
  filter: import('./types').DateFilter
): boolean {
  const a = col.numericValue ? col.numericValue(row) : parseDateValue(col.accessor(row));
  if (!Number.isFinite(a)) return false;
  const aDay = Math.floor(a / MS_PER_DAY);

  if (filter.mode === 'between') {
    const b1 = parseDateValue(filter.value);
    const b2 = filter.value2 ? parseDateValue(filter.value2) : NaN;
    if (!Number.isFinite(b1) || !Number.isFinite(b2)) return false;
    const lo = Math.floor(Math.min(b1, b2) / MS_PER_DAY);
    const hi = Math.floor(Math.max(b1, b2) / MS_PER_DAY);
    return aDay >= lo && aDay <= hi;
  }

  const b = parseDateValue(filter.value);
  if (!Number.isFinite(b)) return false;
  const bDay = Math.floor(b / MS_PER_DAY);
  switch (filter.mode) {
    case 'eq': return aDay === bDay;
    case 'neq': return aDay !== bDay;
    case 'lt': return aDay < bDay;
    case 'gt': return aDay > bDay;
    case 'lte': return aDay <= bDay;
    case 'gte': return aDay >= bDay;
  }
}

export function rulesFor(formats: FormatsState, colId: string): ConditionalFormatRule[] {
  const v = formats[colId];
  if (!v) return [];
  // Backward compat: legacy saved views may still serialize a single rule.
  return Array.isArray(v) ? v : [v as unknown as ConditionalFormatRule];
}

/**
 * Returns the first matched cell-level rule (i.e. `applyToRow=false`), or null.
 * Rules are evaluated in user order — first match wins.
 */
export function cellRule<T>(
  row: T,
  col: ColumnDef<T>,
  formats: FormatsState
): ConditionalFormatRule | null {
  for (const rule of rulesFor(formats, col.id)) {
    if (rule.applyToRow) continue;
    if (matchesRule(row, col, rule)) return rule;
  }
  return null;
}

/**
 * @deprecated The single-color API. Returns the BG color of the matched cell
 * rule — kept temporarily for callers that haven't moved to `cellRule`.
 */
export function cellFormat<T>(
  row: T,
  col: ColumnDef<T>,
  formats: FormatsState
): ColorPreset | null {
  const rule = cellRule(row, col, formats);
  return rule ? (rule.bgColor ?? rule.color ?? null) : null;
}

export function rowFormat<T>(
  row: T,
  columns: ColumnDef<T>[],
  formats: FormatsState
): ColorPreset | null {
  for (const col of columns) {
    for (const rule of rulesFor(formats, col.id)) {
      if (!rule.applyToRow) continue;
      if (matchesRule(row, col, rule)) {
        return rule.bgColor ?? rule.color ?? null;
      }
    }
  }
  return null;
}

export function matchedRules<T>(
  row: T,
  columns: ColumnDef<T>[],
  formats: FormatsState
): { col: ColumnDef<T>; rule: ConditionalFormatRule }[] {
  const out: { col: ColumnDef<T>; rule: ConditionalFormatRule }[] = [];
  for (const col of columns) {
    for (const rule of rulesFor(formats, col.id)) {
      if (matchesRule(row, col, rule)) {
        out.push({ col, rule });
        break; // first match per column wins
      }
    }
  }
  return out;
}
